import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Menu as MenuIcon } from '@mui/icons-material';
import './style.css';
import { unifiedSidebarData } from '../../constants/unifiedSidebarData';
import WHITE_Logo from '../../assets/images/WHITE__Logo.png';
import { useUser } from '../../utils/hooks/user';
import Sider from 'antd/es/layout/Sider';
import { Menu, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import appConfig from '../../constants/app';

import { useFeatureStore } from '../../zustand/features';

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const roles = useUser((user) => user?.roles);
  const features = useFeatureStore((state) => state?.features ?? []);
  const navigate = useNavigate();

  // Helper function to check if user has required role
  const hasRole = useCallback(
    (requiredRoles) => {
      if (!requiredRoles || requiredRoles.length === 0) return true;
      return roles?.some((role) => requiredRoles.includes(role));
    },
    [roles]
  );

  // Helper function to check if tenant has required feature entitlement
  const hasFeature = useCallback(
    (feature) => {
      if (!feature) return true;
      if (Array.isArray(feature)) {
        return feature.some((f) => Array.isArray(features) && features.includes(f));
      }
      return Array.isArray(features) && features.includes(feature);
    },
    [features]
  );

  // Filter sidebar items based on user roles (keep feature-gated items so they can be shown disabled)
  const sidebars = useMemo(() => {
    const filterMenuItems = (items) => {
      return items
        .filter((item) => hasRole(item.roles))
        .map((item) => ({
          ...item,
          children: item.children ? filterMenuItems(item.children) : undefined
        }))
        .filter((item) => !item.children || item.children.length > 0);
    };

    return filterMenuItems(unifiedSidebarData);
  }, [hasRole]);

  // Set of disabled route keys for fast lookup
  const disabledKeys = useMemo(() => {
    const keys = new Set();
    sidebars.forEach((item) => {
      if (item.feature && !hasFeature(item.feature)) {
        keys.add(`/${item.route?.toLowerCase() || ''}`);
      }
      item.children?.forEach((child) => {
        if (child.feature && !hasFeature(child.feature)) {
          keys.add(`/${child.route?.toLowerCase() || ''}`);
        }
      });
    });
    return keys;
  }, [sidebars, hasFeature]);

  const onClick = useCallback(
    ({ key }) => {
      if (disabledKeys.has(key)) {
        return;
      }
      navigate(key.toLowerCase());
    },
    [disabledKeys, navigate]
  );

  // Highlight the most specific matching menu item based on the current path
  const selectedKeys = useMemo(() => {
    const path = window.location?.pathname?.toLowerCase();
    // Find the most specific (longest) matching key from all sidebar items and their children
    let allKeys = sidebars.flatMap((item) => {
      const parentKey = `/${item.route?.toLowerCase() || ''}`;
      if (item.children && item.children.length > 0) {
        return [parentKey, ...item.children.map((child) => `/${child.route?.toLowerCase() || ''}`)];
      }
      return [parentKey];
    });
    // Sort keys by length descending to match the most specific route first
    allKeys = allKeys.sort((a, b) => b.length - a.length);
    const matchedKey = allKeys.find((key) => path.startsWith(key));
    return matchedKey ? [matchedKey] : ['/'];
  }, [window.location?.pathname, sidebars]);

  // Only allow one submenu open at a time
  const handleOpenChange = useCallback((keys) => {
    setOpenKeys(keys.length > 0 ? [keys[keys.length - 1]] : []);
  }, []);

  const toggleSideBar = () => setCollapsed(!collapsed);

  return (
    <Sider
      breakpoint='lg'
      trigger={null}
      collapsible
      collapsed={collapsed}
      onCollapse={(collapsed) => setCollapsed(collapsed)}
      style={{
        overflow: 'auto',
        position: 'sticky',
        top: 0,
        height: '100vh',
        backgroundColor: '#083147'
      }}
      className='sawport-sidebar overscroll-none'
      data-tour='sidebar'>
      <div
        className={`flex justify-center items-center w-full ${collapsed ? 'my-5' : 'mt-5 mb-3'}`}>
        {!collapsed ? (
          <div className='flex items-center justify-center'>
            <Link to='/'>
              <img src={WHITE_Logo} alt='logo' width='100px' height='27px' />
            </Link>
            <MenuIcon
              onClick={toggleSideBar}
              className='ml-4 cursor-pointer'
              style={{
                marginBottom: '4%',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
            />
          </div>
        ) : (
          <img
            className='cursor-pointer self-center'
            onClick={toggleSideBar}
            src={'/favicon.ico'}
            alt='logo'
            height='27px'
          />
        )}
      </div>
      <Menu
        onClick={onClick}
        className='sidebar-navigation mb-6 bg-[#083147]'
        theme='dark'
        mode='inline'
        defaultSelectedKeys={['/']}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={sidebars.map(({ Icon, route, title, navLabel, children, badge, feature }, index) => {
          const isItemDisabled = Boolean(feature && !hasFeature(feature));

          const renderItemLabel = (labelTitle, labelBadge, isLocked, isChild = false, menuTitle = labelTitle) => {
            const labelContent = (
              <span
                className={`sidebar-menu-label${isLocked ? ' sidebar-menu-label--locked' : ''}${labelBadge ? ' sidebar-menu-label--has-badge' : ''}`}
                data-tour={!isChild ? `sidebar-${menuTitle.toLowerCase().replace(/\s+/g, '-')}` : undefined}>
                <span className='sidebar-menu-label__main'>
                  <span className='sidebar-menu-label__text' title={menuTitle}>{labelTitle}</span>
                  {labelBadge && <span className='sidebar-menu-label__badge'>{labelBadge}</span>}
                </span>
                <span className='sidebar-menu-label__meta'>
                  {isLocked && (
                    <LockOutlined className='sidebar-menu-label__lock' aria-label='Locked' />
                  )}
                </span>
              </span>
            );

            if (isLocked) {
              return (
                <Tooltip
                  title='This feature is not enabled for your organization. Ask your administrator to enable it.'
                  placement='right'>
                  {labelContent}
                </Tooltip>
              );
            }

            return labelContent;
          };

          const renderItemIcon = (isLocked) => {
            const icon = <Icon width={20} height={20} style={{ fontSize: 20 }} />;
            if (!isLocked || !collapsed) return icon;

            const lockedIcon = (
              <span className='sidebar-collapsed-lock'>
                {icon}
                <LockOutlined className='sidebar-collapsed-lock__marker' aria-label='Locked' />
              </span>
            );

            return (
              <Tooltip
                title={`${title} is locked. Ask your administrator to enable it.`}
                placement='right'>
                {lockedIcon}
              </Tooltip>
            );
          };

          return {
            key: children?.length
              ? `/${route?.toLowerCase() || ''}/__menu`
              : `/${route?.toLowerCase() || ''}`,
            icon: renderItemIcon(isItemDisabled),
            disabled: isItemDisabled,
            title: isItemDisabled ? `${title} (Not enabled on your plan)` : title,
            label: renderItemLabel(isItemDisabled ? navLabel || title : title, badge, isItemDisabled, false, title),
            children: children?.map(
              ({ Icon: ChildIcon, route: childRoute, title: childTitle, badge: childBadge, feature: childFeature }) => {
                const isChildDisabled = Boolean(childFeature && !hasFeature(childFeature));

                return {
                  key: `/${childRoute?.toLowerCase() || ''}`,
                  disabled: isChildDisabled,
                  title: isChildDisabled ? `${childTitle} (Not enabled on your plan)` : childTitle,
                  icon: <ChildIcon width={20} height={20} style={{ fontSize: 20 }} />,
                  label: renderItemLabel(childTitle, childBadge, isChildDisabled, true)
                };
              }
            )
          };
        })}
      />

      {!collapsed && (
        <p className='text-center absolute bottom-0 left-0 right-0 text-[#bad5e6]'>
          Version {appConfig.version}
        </p>
      )}
    </Sider>
  );
};

export default SideBar;
